import React, { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useMutation } from "../../hooks/useExclusiveMutation";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { companiesApi, invoicesApi } from "../../api";
import BusinessInfoPanel from "../../components/events/BusinessInfoPanel";
import { CurrencyInput } from "../../components/CurrencyInput";
import { QueryErrorBanner } from "../../components/feedback/QueryFeedback";
import {
  createClearedInvoiceProfile,
  createEmptyLineItem,
  profileFromCompany,
  saveInvoicePrintProfile,
  type InvoiceFormProfile,
  type InvoiceLineItemForm,
} from "../../utils/invoicePrintProfile";
import { missingInvoiceInfo } from "../../utils/companyInvoiceInfo";
import { useAuth } from "../../context/AuthContext";
import { workspaceNav, writeLastEventId } from "../../routes/workspaceNavigation";
import { queryKeys } from "../../queryKeys";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import "./EventInvoicesPage.css";

type Props = {
  workspace: WorkspaceId;
  embedMode?: boolean;
};

function patchProfile(prev: InvoiceFormProfile, patch: Partial<InvoiceFormProfile>): InvoiceFormProfile {
  return { ...prev, ...patch };
}

/**
 * The backend still bills one tax rate per invoice — there's no per-line GST
 * split server-side. Line items each carry their own rate for display on the
 * PDF, so the first one that's filled in is what actually drives the CGST/
 * SGST math.
 */
function deriveTaxRate(lineItems: InvoiceLineItemForm[]): number {
  const first = lineItems.find((item) => item.tax_rate.trim() !== "");
  const parsed = first ? Number(first.tax_rate) : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Particulars, Quantity and Rate are marked required; without this a blank line is silently dropped from the invoice. */
function lineItemError(lineItems: InvoiceLineItemForm[]): string | null {
  for (let i = 0; i < lineItems.length; i += 1) {
    const item = lineItems[i];
    const label = `Line ${i + 1}`;
    if (!item.description.trim()) return `${label}: enter the particulars.`;
    if (item.quantity.trim() === "" || !(Number(item.quantity) > 0)) return `${label}: enter a quantity greater than 0.`;
    if (item.unit_price.trim() === "" || !(Number(item.unit_price) >= 0)) return `${label}: enter a rate.`;
  }
  return null;
}

function syncConsigneeFromBuyer(profile: InvoiceFormProfile): InvoiceFormProfile {
  if (!profile.consigneeSameAsBuyer) return profile;
  return {
    ...profile,
    consigneeName: profile.buyerName,
    consigneeAddress: profile.buyerAddress,
    consigneeState: profile.buyerState,
    consigneeStateCode: profile.buyerStateCode,
  };
}

const EventInvoiceGeneratePage: React.FC<Props> = ({ workspace, embedMode = false }) => {
  const { token } = useAuth();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeDraftId = searchParams.get("draft");
  const queryClient = useQueryClient();

  const [profile, setProfile] = useState<InvoiceFormProfile>(() => createClearedInvoiceProfile());
  // Set once a draft exists, so subsequent saves patch it instead of creating
  // a new one on every click.
  const [draftId, setDraftId] = useState<string | null>(null);

  const updateProfile = useCallback((patch: Partial<InvoiceFormProfile>) => {
    setProfile((prev) => syncConsigneeFromBuyer(patchProfile(prev, patch)));
  }, []);

  useEffect(() => {
    if (eventId) {
      writeLastEventId(workspace, eventId);
    }
  }, [eventId, workspace]);

  const companyQuery = useQuery({
    queryKey: token ? ["company-me", token] : ["company-me", "idle"],
    queryFn: () => companiesApi.me(token!),
    enabled: Boolean(token),
  });

  const invoicesQuery = useQuery({
    queryKey: eventId && token ? queryKeys.invoices(workspace, eventId) : queryKeys.events.all(workspace),
    queryFn: () => invoicesApi.listByEvent(eventId!, token!),
    enabled: Boolean(eventId && token),
  });


  // Line items are normalised the same way for drafts and for generate, so a
  // draft's running total matches what the finalized invoice will show.
  const buildLineItems = useCallback((source: InvoiceFormProfile) => {
    return source.lineItems
      .map((item) => {
        const quantity = Number(item.quantity);
        const unit_price = Number(item.unit_price);
        const taxRate = item.tax_rate.trim() !== "" ? Number(item.tax_rate) : undefined;
        return {
          description: item.description.trim(),
          quantity: Number.isFinite(quantity) ? quantity : 0,
          unit_price: Number.isFinite(unit_price) ? unit_price : 0,
          amount:
            Number.isFinite(quantity) && Number.isFinite(unit_price)
              ? Math.round(quantity * unit_price * 100) / 100
              : 0,
          // Per-line rate; server falls back to the invoice default when unset.
          tax_rate: taxRate !== undefined && Number.isFinite(taxRate) ? taxRate : undefined,
        };
      })
      .filter((item) => item.description && item.quantity > 0);
  }, []);

  const draftMutation = useMutation({
    mutationFn: async () => {
      if (!eventId || !token) throw new Error("Missing event context.");
      // Seller/bank identity has no input on this form (BusinessInfoPanel only
      // displays it) — it lives on the Company profile and must be merged in
      // here or the saved form_data snapshot has a permanently blank seller.
      const withSeller = companyQuery.data ? profileFromCompany(companyQuery.data, profile) : profile;
      const synced = syncConsigneeFromBuyer(withSeller);
      const payload = {
        vendor_name: synced.buyerName.trim() || undefined,
        vendor_address: synced.buyerAddress.trim() || undefined,
        consignee_name: synced.consigneeName.trim() || synced.buyerName.trim() || undefined,
        category: synced.category.trim() || undefined,
        tax_rate: deriveTaxRate(synced.lineItems),
        due_date: synced.dueDate || undefined,
        line_items: buildLineItems(synced),
        form_data: synced as unknown as Record<string, unknown>,
      };
      return draftId
        ? invoicesApi.updateDraft(draftId, payload, token)
        : invoicesApi.createDraft({ ...payload, event_id: eventId }, token);
    },
    onSuccess: async (invoice) => {
      setDraftId(invoice.id);
      toast.success("Draft saved");
      if (eventId) {
        await queryClient.invalidateQueries({ queryKey: ["invoices", workspace, eventId] });
      }
    },
    onError: (err) => toast.error(err instanceof Error ? err.message : "Could not save draft"),
  });

  const handleSaveDraft = async () => {
    try {
      await draftMutation.mutateAsync();
    } catch {
      // Surfaced by onError; swallowed so the click handler stays quiet.
    }
  };

  // "Continue editing" on the Manage Invoices tab deep-links back here with
  // ?draft=<id>; rehydrate the form from the persisted form_data.
  useEffect(() => {
    if (!resumeDraftId || !token || draftId === resumeDraftId) return;
    let cancelled = false;
    void (async () => {
      try {
        const detail = await invoicesApi.detail(resumeDraftId, token);
        if (cancelled) return;
        if (detail.form_data) {
          // Merge over a fresh default profile so drafts saved before a form
          // field existed (e.g. due date) don't leave it `undefined`.
          setProfile({
            ...createClearedInvoiceProfile(),
            ...(detail.form_data as Partial<InvoiceFormProfile>),
          });
        }
        setDraftId(detail.id);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Could not open draft");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [draftId, resumeDraftId, token]);

  const generateMutation = useMutation({
    mutationFn: async () => {
      if (!eventId || !token) throw new Error("Missing event context.");
      // Seller/bank identity has no input on this form (BusinessInfoPanel only
      // displays it) — it lives on the Company profile and must be merged in
      // here or the saved form_data snapshot has a permanently blank seller.
      const withSeller = companyQuery.data ? profileFromCompany(companyQuery.data, profile) : profile;
      const synced = syncConsigneeFromBuyer(withSeller);
      const payloadLineItems = buildLineItems(synced);

      // The invoice number is assigned by the server under a row lock. The
      // client used to compute it from its own cached list and send it, but
      // the field was absent from the request schema and silently dropped —
      // two open tabs could also mint the same number.
      return invoicesApi.generate(
        {
          event_id: eventId,
          vendor_name: synced.buyerName.trim() || undefined,
          vendor_address: synced.buyerAddress.trim() || undefined,
          consignee_name: synced.consigneeName.trim() || synced.buyerName.trim() || undefined,
          category: synced.category.trim() || undefined,
          tax_rate: deriveTaxRate(synced.lineItems),
          due_date: synced.dueDate || undefined,
          line_items: payloadLineItems.length > 0 ? payloadLineItems : undefined,
          form_data: synced as unknown as Record<string, unknown>,
        },
        token
      );
    },
    onSuccess: async () => {
      if (!eventId) return;
      await queryClient.invalidateQueries({ queryKey: queryKeys.invoices(workspace, eventId) });
    },
  });


  const addLineItem = () => {
    setProfile((prev) => ({
      ...prev,
      lineItems: [...prev.lineItems, createEmptyLineItem()],
    }));
  };

  const updateLineItem = (id: string, patch: Partial<InvoiceLineItemForm>) => {
    setProfile((prev) => ({
      ...prev,
      lineItems: prev.lineItems.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    }));
  };

  const removeLineItem = (id: string) => {
    setProfile((prev) =>
      prev.lineItems.length > 1
        ? { ...prev, lineItems: prev.lineItems.filter((item) => item.id !== id) }
        : prev
    );
  };

  const resetInvoiceForm = useCallback(() => {
    setProfile(createClearedInvoiceProfile());
  }, []);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    const synced = syncConsigneeFromBuyer(profile);
    if (!companyQuery.data?.name?.trim()) {
      toast.error("Add your business name in Profile & Settings before generating invoices.");
      return;
    }
    const missingInfo = missingInvoiceInfo(companyQuery.data);
    if (missingInfo.length > 0) {
      toast.error(
        `Complete Invoice information in Profile & Settings before generating invoices. Missing: ${missingInfo.join(", ")}.`
      );
      return;
    }
    if (!synced.buyerName.trim()) {
      toast.error("Enter your client or customer name (bill to).");
      return;
    }
    const lineError = lineItemError(synced.lineItems);
    if (lineError) {
      toast.error(lineError);
      return;
    }
    try {
      // Working from a saved draft: patch it and finalize in place. Calling
      // generate would issue a second invoice and strand the draft.
      const invoice = draftId
        ? await (async () => {
            await draftMutation.mutateAsync();
            return invoicesApi.finalizeDraft(draftId, token!);
          })()
        : (await generateMutation.mutateAsync()).invoice;

      saveInvoicePrintProfile(invoice.id, synced);
      setDraftId(null);
      resetInvoiceForm();
      toast.success(`Invoice ${invoice.invoice_number} generated`);
      // The list now lives on the Manage Invoices sub-tab — take the user to
      // the invoice they just created rather than leaving them on a cleared form.
      navigate(`/events/${eventId}/invoices/manage`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to generate invoice");
    }
  };




  const handleClearForm = () => {
    resetInvoiceForm();
    toast.success("Form cleared");
  };

  if (!eventId) {
    return (
      <div className="app-shell-dash event-invoices-page">
        <h1 className="app-shell-page-title">Event invoices</h1>
        <p className="app-shell-lede">Open this page from an event route.</p>
      </div>
    );
  }

  return (
    <div className="app-shell-dash event-invoices-page event-invoices-page--fit">
      {!embedMode ? (
        <>
      <Link to={workspaceNav.dashboard(workspace, eventId)} className="app-shell-back-link">
        ← Back to dashboard
      </Link>
      <div className="app-shell-dash-toolbar">
        <div>
          <h1 className="app-shell-page-title event-invoices-page__title">Tax invoices</h1>
          <p className="app-shell-lede event-invoices-page__lede">
            Zoniq is your billing platform — you issue invoices under your own business for any service or client.
            Enter your legal seller details below; they appear on the PDF, not Zoniq.
          </p>
        </div>
      </div>
        </>
      ) : null}

      {invoicesQuery.isError ? (
        <QueryErrorBanner
          message={invoicesQuery.error instanceof Error ? invoicesQuery.error.message : "Failed to load invoices"}
          onRetry={() => void invoicesQuery.refetch()}
        />
      ) : null}

      <BusinessInfoPanel company={companyQuery.data} loading={companyQuery.isPending} />

      <div className="eventos-card event-invoices-page__form-card">
        <div className="event-invoices-page__form-head">
          <div className="eventos-label">Generate tax invoice</div>
          <label className="eventos-field event-invoices-page__due-date">
            <span className="eventos-field-label">Due date</span>
            <input
              className="eventos-input"
              type="date"
              value={profile.dueDate}
              onChange={(e) => updateProfile({ dueDate: e.target.value })}
            />
          </label>
          <button type="button" className="eventos-nav-btn event-invoices-page__clear-btn" onClick={handleClearForm}>
            Clear
          </button>
        </div>

        <form onSubmit={handleGenerate} className="eventos-form event-invoices-page__form">
          <div className="event-invoices-page__form-scroll">
            <div className="event-invoices-page__pdf-layout">

              <div className="event-invoices-page__parties">
                <section className="event-invoices-page__section" aria-labelledby="buyer-heading">
                  <h2 id="buyer-heading" className="event-invoices-page__section-title">
                    Buyer (Bill to)
                  </h2>
                  <div className="eventos-form-grid">
                    <label className="eventos-field event-invoices-page__field-full">
                      <span className="eventos-field-label">Name *</span>
                      <input
                        className="eventos-input"
                        value={profile.buyerName}
                        onChange={(e) => updateProfile({ buyerName: e.target.value })}
                        required
                      />
                    </label>
                    <label className="eventos-field event-invoices-page__field-full">
                      <span className="eventos-field-label">Address</span>
                      <textarea
                        className="eventos-input"
                        value={profile.buyerAddress}
                        onChange={(e) => updateProfile({ buyerAddress: e.target.value })}
                        rows={2}
                      />
                    </label>
                    <label className="eventos-field">
                      <span className="eventos-field-label">State name</span>
                      <input
                        className="eventos-input"
                        value={profile.buyerState}
                        onChange={(e) => updateProfile({ buyerState: e.target.value })}
                      />
                    </label>
                    <label className="eventos-field">
                      <span className="eventos-field-label">State code</span>
                      <input
                        className="eventos-input"
                        value={profile.buyerStateCode}
                        onChange={(e) => updateProfile({ buyerStateCode: e.target.value })}
                      />
                    </label>
                  </div>
                </section>

                <section className="event-invoices-page__section" aria-labelledby="consignee-heading">
                  <div className="event-invoices-page__section-head">
                    <h2 id="consignee-heading" className="event-invoices-page__section-title">
                      Consignee (Ship to)
                    </h2>
                    <label className="event-invoices-page__checkbox">
                      <input
                        type="checkbox"
                        checked={profile.consigneeSameAsBuyer}
                        onChange={(e) => updateProfile({ consigneeSameAsBuyer: e.target.checked })}
                      />
                      Same as buyer
                    </label>
                  </div>
                  {!profile.consigneeSameAsBuyer ? (
                    <div className="eventos-form-grid">
                      <label className="eventos-field event-invoices-page__field-full">
                        <span className="eventos-field-label">Name</span>
                        <input
                          className="eventos-input"
                          value={profile.consigneeName}
                          onChange={(e) => updateProfile({ consigneeName: e.target.value })}
                        />
                      </label>
                      <label className="eventos-field event-invoices-page__field-full">
                        <span className="eventos-field-label">Address</span>
                        <textarea
                          className="eventos-input"
                          value={profile.consigneeAddress}
                          onChange={(e) => updateProfile({ consigneeAddress: e.target.value })}
                          rows={2}
                        />
                      </label>
                      <label className="eventos-field">
                        <span className="eventos-field-label">State name</span>
                        <input
                          className="eventos-input"
                          value={profile.consigneeState}
                          onChange={(e) => updateProfile({ consigneeState: e.target.value })}
                        />
                      </label>
                      <label className="eventos-field">
                        <span className="eventos-field-label">State code</span>
                        <input
                          className="eventos-input"
                          value={profile.consigneeStateCode}
                          onChange={(e) => updateProfile({ consigneeStateCode: e.target.value })}
                        />
                      </label>
                    </div>
                  ) : (
                    <p className="event-invoices-page__same-hint">Ship-to matches bill-to on the PDF.</p>
                  )}
                </section>
              </div>

              <section className="event-invoices-page__section" aria-labelledby="line-items-heading">
                <div className="event-invoices-page__section-head">
                  <h2 id="line-items-heading" className="event-invoices-page__section-title">
                    Particulars (line items)
                  </h2>
                  <button type="button" className="eventos-nav-btn event-invoices-page__add-line-btn" onClick={addLineItem}>
                    + Add line
                  </button>
                </div>
                <div className="event-invoices-page__line-items">
                  {profile.lineItems.map((item, index) => (
                    <div key={item.id} className="event-invoices-page__line-item-card">
                      <div className="event-invoices-page__line-item-head">
                        <span className="event-invoices-page__line-item-index">Sl No. {index + 1}</span>
                        <button
                          type="button"
                          className="eventos-nav-btn"
                          disabled={profile.lineItems.length === 1}
                          onClick={() => removeLineItem(item.id)}
                        >
                          Remove
                        </button>
                      </div>
                      <div className="eventos-form-grid event-invoices-page__line-grid">
                        <label className="eventos-field event-invoices-page__field-full">
                          <span className="eventos-field-label">Particulars *</span>
                          <input
                            className="eventos-input"
                            value={item.description}
                            onChange={(e) => updateLineItem(item.id, { description: e.target.value })}
                          />
                        </label>
                        <label className="eventos-field">
                          <span className="eventos-field-label">HSN/SAC</span>
                          <input
                            className="eventos-input"
                            value={item.hsn_sac}
                            onChange={(e) => updateLineItem(item.id, { hsn_sac: e.target.value })}
                            placeholder={profile.category || "Optional"}
                          />
                        </label>
                        <label className="eventos-field">
                          <span className="eventos-field-label">Quantity *</span>
                          <input
                            className="eventos-input"
                            type="number"
                            min={1}
                            value={item.quantity}
                            onChange={(e) => updateLineItem(item.id, { quantity: e.target.value })}
                            onFocus={(e) => e.target.select()}
                            placeholder="1"
                          />
                        </label>
                        <label className="eventos-field">
                          <span className="eventos-field-label">Rate *</span>
                          <CurrencyInput
                            value={item.unit_price}
                            onChange={(e) => updateLineItem(item.id, { unit_price: e.target.value })}
                            onFocus={(e) => e.target.select()}
                            placeholder="0.00"
                          />
                        </label>
                        <label className="eventos-field">
                          <span className="eventos-field-label">Tax rate (%)</span>
                          <input
                            className="eventos-input"
                            type="number"
                            min={0}
                            step="0.01"
                            value={item.tax_rate}
                            onChange={(e) => updateLineItem(item.id, { tax_rate: e.target.value })}
                            onFocus={(e) => e.target.select()}
                            placeholder="18"
                          />
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>

          <div className="event-invoices-page__submit-row">
            <button className="eventos-primary-btn" type="submit" disabled={generateMutation.isPending}>
              {generateMutation.isPending ? "Generating…" : "Generate invoice"}
            </button>
            {/* Deliberately type="button": a draft must save whatever has been
                filled in so far, without tripping the form's validation. */}
            <button
              type="button"
              className="eventos-nav-btn"
              disabled={draftMutation.isPending}
              onClick={() => void handleSaveDraft()}
            >
              {draftMutation.isPending ? "Saving…" : draftId ? "Update draft" : "Save as draft"}
            </button>
            {draftId ? (
              <span className="eventos-subtle event-invoices-page__draft-note">
                Editing a saved draft — it stays out of financial totals until you generate it.
              </span>
            ) : null}
          </div>
        </form>
      </div>

    </div>
  );
};

export default EventInvoiceGeneratePage;
